"use client"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Clock, Target, AlertTriangle, TrendingUp } from "lucide-react"
import {Link} from "@tanstack/react-router"
import { OwnerViewData } from "@/hooks/user-owner-data"

interface TeamMemberJob {
  id?: string
  title?: string
  status?: string
  longlist?: number
  interviewing?: number
  hired?: number
}

const getRiskColor = (risk: string) => {
  switch (risk) {
    case "high":
      return "bg-red-100 text-red-800 border-red-200"
    case "medium":
      return "bg-yellow-100 text-yellow-800 border-yellow-200"
    case "low":
      return "bg-green-100 text-green-800 border-green-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}



const TeamMemberJobCard = ({ job }: { job: TeamMemberJob }) => {
  return (
    <div className="space-y-2 p-3 bg-white dark:bg-gray-800 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between">
        <Link to={`/jobs/${job.id}/overview`} className="text-sm font-medium hover:text-brand-ink hover:underline">
          {job.title}
        </Link>
        <Badge variant={job.status === "active" ? "default" : "secondary"} className="text-xs">
          {job.status}
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <div className="font-medium text-blue-600">{job.longlist}</div>
          <div className="text-muted-foreground">Longlist</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-yellow-600">{job.interviewing}</div>
          <div className="text-muted-foreground">Interviewing</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-green-600">{job.hired}</div>
          <div className="text-muted-foreground">Hired</div>
        </div>
      </div>
    </div>
  )
}

export function OwnerView({
  orgMetrics,
  riskJobs,
  leaderboard,
  teamMembersPipelines,
  organizationGameMode,
}: OwnerViewData & {organizationGameMode:boolean}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Requisitions</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgMetrics?.openReqs}</div>
            <p className="text-xs text-muted-foreground">+3 this week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Time to Fill</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgMetrics?.avgTimeToFill} days</div>
            <p className="text-xs text-muted-foreground">-2 days from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">At-Risk Jobs</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{orgMetrics?.riskJobs}</div>
            <p className="text-xs text-muted-foreground">Require attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Performance</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgMetrics?.teamPerformance??' '}%</div>
            <p className="text-xs text-muted-foreground">+5% this quarter</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Risk Heat Map</CardTitle>
            <CardDescription>Jobs requiring immediate attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {riskJobs?.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Link to={`/jobs/${item.id}/overview`} className="font-medium hover:text-brand-ink hover:underline">
                      {item.job}
                    </Link>
                    <p className="text-sm text-muted-foreground">{item.reason}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={getRiskColor(item.risk??'')}>{item.risk} risk</Badge>
                    <p className="text-xs text-muted-foreground mt-1">{item.days} days open</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {organizationGameMode && (
          <Card>
            <CardHeader>
              <CardTitle>Full Team Leaderboard</CardTitle>
              <CardDescription>All recruiters this month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard?.map((member) => (
                  <div key={member.rank} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="w-6 text-center font-medium">#{member.rank}</span>
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {(member.name as string)
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{member.name}</span>
                      {member.badge && <span className="text-lg">{member.badge}</span>}
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{member.xp} XP</div>
                      <div className="text-xs text-muted-foreground">{member.hires} hires</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team Members Pipelines</CardTitle>
          <CardDescription>Active jobs and pipeline progress for each team member</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {teamMembersPipelines?.map((member) => (
              <div key={member.id} className="space-y-3">
                <div className="flex items-center space-x-3 pb-2 border-b">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-sm">
                      {(member.name as string)
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h4 className="font-semibold">{member.name}</h4>
                    <p className="text-xs text-muted-foreground">{member.jobs?.length} active jobs</p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  {member.jobs?.map((job) => (
                    <TeamMemberJobCard key={job.id} job={job} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
